// src/components/MultipleFileUpload.js
import React, { useState, useEffect } from 'react';
import { Button, List, ListItem, ListItemText, IconButton, Dialog, DialogContent, DialogTitle  } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';

const MultipleFileUpload = ({ label, initialFiles = [], onFilesChange }) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [filesToDelete, setFilesToDelete] = useState([]); // DB에 저장된 파일 중 삭제 요청된 목록
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState('');

  // 초기 상태 설정 (모달 열릴 때 defaultFiles가 바뀌면 내부 상태도 초기화)
  useEffect(() => {
    setSelectedFiles(initialFiles);
    setFilesToDelete([]); // 모달 열릴 때 삭제 목록 초기화
    if (onFilesChange) onFilesChange(initialFiles, []); // 초기 상태 전달
  }, [initialFiles, onFilesChange]);

  // 파일 선택 시 기존 파일 목록에 새 파일 추가
  const handleFileSelect = (e) => {
    const newFiles = Array.from(e.target.files);
    setSelectedFiles((prevFiles) => {
      const updatedFiles = [...prevFiles, ...newFiles];
      if (onFilesChange) onFilesChange(updatedFiles);
      return updatedFiles;
    });
    // input 값을 리셋하여 동일 파일 재선택 가능
    e.target.value = '';
  };

  // 파일 삭제 처리
  const handleFileDelete = (index) => {
    const file = selectedFiles[index];
    if (file instanceof File) {
      // 신규 파일은 바로 제거
      const updatedFiles = selectedFiles.filter((_, i) => i !== index);
      setSelectedFiles(updatedFiles);
      if (onFilesChange) onFilesChange(updatedFiles, filesToDelete);
    } else if (file.filePath) {
      // DB에 업로드된 파일인 경우 삭제 확인 후 처리
      const confirmDelete = window.confirm("이 파일을 삭제하시겠습니까?");
      if (confirmDelete) {
        const updatedFiles = selectedFiles.filter((_, i) => i !== index);
        const updatedToDelete = [...filesToDelete, file.filePath];
        setSelectedFiles(updatedFiles);
        setFilesToDelete(updatedToDelete);
        if (onFilesChange) onFilesChange(updatedFiles, updatedToDelete);
      }
    }
  };

  // 파일이 이미지인지 판별 (File 객체인 경우 file.type 사용, 이미 업로드된 경우 filePath의 확장자로 체크)
  const isImageFile = (file) => {
    if (file instanceof File) {
      return file.type.startsWith('image/');
    } else if (file.filePath) {
      const ext = file.filePath.split('.').pop().toLowerCase();
      return ['jpg', 'jpeg', 'png', 'gif', 'bmp'].includes(ext);
    }
    return false;
  };

  // 미리보기용 URL 생성 (File 객체는 URL.createObjectURL, 이미 업로드된 파일은 filePath 사용)
  const getPreviewUrl = (file) => {
    if (file instanceof File) {
      return URL.createObjectURL(file);
    } else if (file.filePath) {
      console.log(file.filePath);
      return file.filePath;
    }
    return '';
  };

  // 이미지 클릭 시 미리보기 모달 열기
  const handlePreview = (file) => {
    if (isImageFile(file)) {
      const url = getPreviewUrl(file);
      setPreviewImage(url);
      setPreviewOpen(true);
    }
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setPreviewImage('');
  };

  //파일 다운로드
  const handleFileDownload = async (file) => {
    
    // file.filePath는 DB에 저장된 상대경로라고 가정 (예: "/data/drawings/25-03/파일명.PNG")
    const url = `/api/files/download?filePath=${encodeURIComponent(file.filePath)}`;
    
    try {
      const response = await fetch(url, {
        method: 'GET',
        // 필요한 경우 인증 헤더 등을 추가
      });
      if (!response.ok) {
        throw new Error("파일 다운로드 실패");
      }
      // API가 파일 Blob을 반환한다고 가정합니다.
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      
      // 임시 a 엘리먼트를 생성해 다운로드를 트리거
      const link = document.createElement('a');
      link.href = downloadUrl;
      // 파일 이름은 DB에 저장된 fileName 사용 (필요에 따라 getDisplayFileName 활용)
      const filename = getDisplayFileName(file.fileName);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      // 생성된 Object URL 해제
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error("다운로드 오류:", error);
    }
  };

  // 파일명에서 UUID와 업로드시각 제거하여 원본 이름과 확장자만 추출하는 유틸리티 함수
  const getDisplayFileName = (fileName) => {
    // 정규표현식: 앞부분은 원본 파일명, 뒤에 UUID (36자리, 하이픈 포함)와 업로드시각(숫자) 그리고 확장자
    // 수정된 정규표현식:
    // - 그룹1: 원본 파일명
    // - 그룹2: UUID (36자리, 하이픈 포함)
    // - (선택적) 그룹3: 업로드시각 (숫자, 있을 경우)
    // - 그룹4: 확장자
    const regex = /^(.*)_([0-9a-fA-F\-]{36})(?:_([0-9]+))?(\.[^.]+)$/;
    const match = fileName.match(regex);
    if (match) {
      // match[1]: 원본 파일명, match[4]: 확장자
      return match[1] + match[4];
    }
    return fileName;
  };

  return (
    <div>
      <input
        type="file"
        multiple
        onChange={handleFileSelect}
        style={{ display: 'none' }}
        id={`${label}-file-input`}
      />
      <label htmlFor={`${label}-file-input`}>
        <Button variant="contained" component="span">
          {label}
        </Button>
      </label>
      <List>
        {selectedFiles.map((file, index) => {
           // 저장된 파일 객체는 fileName, 새 File 객체는 name 프로퍼티 사용
           const originalName = file.fileName ? file.fileName : file.name;
           const displayName = getDisplayFileName(originalName);
           return(
            <ListItem
              key={index}
              style={{ display: 'flex', alignItems: 'center' }}
            >
              {isImageFile(file) && (
                <img
                  src={getPreviewUrl(file)}
                  alt={displayName}
                  style={{ width: 50, height: 50, objectFit: 'cover', marginRight: 8, cursor: 'pointer' }}
                  onClick={() => handlePreview(file)}
                />
              )}
              <ListItemText primary={
                file.filePath ? (
                <span
                  onClick={() => handleFileDownload(file)}
                  style={{ cursor: 'pointer', color: 'blue', textDecoration: 'underline' }}
                >
                {displayName}
                </span>
                ) : (
                  <span>{displayName}</span>
                )
                } />
              <IconButton edge="end" onClick={() => handleFileDelete(index)}>
                <DeleteIcon />
              </IconButton>
            </ListItem>
          );
        })}
      </List>
      <Dialog open={previewOpen} onClose={handleClosePreview} maxWidth="md" fullWidth>
        <DialogTitle>이미지 미리보기</DialogTitle>
        <DialogContent>
          <img src={previewImage} alt="Preview" style={{ width: '100%' }} />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MultipleFileUpload;
